/**
 * FORM TEXTAREA COMPONENT
 *
 * Reusable textarea with consistent styling.
 * Supports labels, help text, error states, and character limits.
 *
 * Features:
 * - Dark/light variants
 * - Label and help text
 * - Error state with message
 * - Character count
 * - Auto-resize option
 *
 * @example
 * <FormTextarea
 *   label="Notes"
 *   value={notes}
 *   onChange={(e) => setNotes(e.target.value)}
 *   placeholder="Add any notes..."
 *   rows={3}
 *   maxLength={500}
 *   showCharCount
 * />
 */

import { type TextareaHTMLAttributes, forwardRef } from 'react';

export type TextareaVariant = 'light' | 'dark';

export interface FormTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Label text above textarea */
  label?: string;
  /** Help text below textarea */
  helpText?: string;
  /** Error message (also sets error styling) */
  error?: string;
  /** Color variant */
  variant?: TextareaVariant;
  /** Show character count (requires maxLength) */
  showCharCount?: boolean;
  /** Additional class for wrapper */
  wrapperClassName?: string;
  /** Additional class for textarea */
  className?: string;
}

const variantClasses: Record<TextareaVariant, { textarea: string; label: string; help: string }> = {
  light: {
    textarea: 'border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-purple-500 focus:border-purple-500',
    label: 'text-gray-700',
    help: 'text-gray-500',
  },
  dark: {
    textarea: 'border-slate-600 bg-slate-700/50 text-glass placeholder-slate-400 focus:ring-purple-500 focus:border-transparent',
    label: 'text-glass-secondary',
    help: 'text-glass-muted',
  },
};

export const FormTextarea = forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  (
    {
      label,
      helpText,
      error,
      variant = 'light',
      showCharCount = false,
      wrapperClassName = '',
      className = '',
      id,
      value,
      maxLength,
      ...props
    },
    ref
  ) => {
    const textareaId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const styles = variantClasses[variant];
    const hasError = !!error;
    const currentLength = typeof value === 'string' ? value.length : 0;

    const textareaClasses = [
      'w-full rounded-lg border px-3 py-2 transition-colors resize-none',
      'focus:outline-none focus:ring-2',
      styles.textarea,
      hasError ? 'border-red-500 focus:ring-red-500' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClassName}>
        {label && (
          <label
            htmlFor={textareaId}
            className={`block text-sm font-medium mb-1 ${styles.label}`}
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <textarea
          ref={ref}
          id={textareaId}
          className={textareaClasses}
          value={value}
          maxLength={maxLength}
          aria-invalid={hasError}
          aria-describedby={error ? `${textareaId}-error` : helpText ? `${textareaId}-help` : undefined}
          {...props}
        />

        <div className="flex justify-between mt-1">
          <div>
            {error && (
              <p id={`${textareaId}-error`} className="text-sm text-red-500">
                {error}
              </p>
            )}

            {helpText && !error && (
              <p id={`${textareaId}-help`} className={`text-sm ${styles.help}`}>
                {helpText}
              </p>
            )}
          </div>

          {showCharCount && maxLength && (
            <span className={`text-sm ${styles.help}`}>
              {currentLength}/{maxLength}
            </span>
          )}
        </div>
      </div>
    );
  }
);

FormTextarea.displayName = 'FormTextarea';

export default FormTextarea;
